import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import {
  TbSearch,
  TbTrash,
  TbEye,
  TbFocus2,
  TbChevronDown,
  TbDotsVertical,
  TbChevronUp,
  TbRefresh,
  TbDownload,
  TbCalendar,
  TbX,
} from "react-icons/tb";
import { useTaskContext } from "@/context/taskContext";
import { useMapContext } from "@/hooks/useMap";
import { useAllUsers } from "@/hooks/useUsers";
import { useTeams } from "@/hooks/useTeams";
import { useModalStore } from "@/stores/modalStore";
import { zoomToTask } from "@/components/map/taskUtils";
import type { Task } from "@/types/task";
import { fallbackPriorities, fallbackStatuses } from "@/constant/fallbackData";
import { CalendarModal } from "@/components/calendar";
interface TaskTableProps {
  onCreateTask: () => void;
  onViewTask: (task: Task) => void;
  onTaskSelect?: (task: Task | null) => void;
}

export const TaskTable: React.FC<TaskTableProps> = ({
  onViewTask,
  onTaskSelect,
}) => {
  const { tasks, loading, deleteTask, priorities, statuses, loadTasks } =
    useTaskContext();
  const { mapInstance, setTableVisible } = useMapContext();
  const { data: users = [] } = useAllUsers();
  const { data: teams = [] } = useTeams();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPriority, setSelectedPriority] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedTeam, setSelectedTeam] = useState("");

  // Searchable dropdown states
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [teamSearchTerm, setTeamSearchTerm] = useState("");
  const {
    isCalendarOpen,
    isUserDropdownOpen,
    isTeamDropdownOpen,
    toggleCalendar,
    toggleUserDropdown,
    toggleTeamDropdown,
    closeUserDropdown,
    closeTeamDropdown,
    closeCalendar,
  } = useModalStore();

  // Sorting states
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Refs for dropdown containers
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const teamDropdownRef = useRef<HTMLDivElement>(null);

  // Use fallback data if main data isn't available
  const availablePriorities =
    priorities.length > 0 ? priorities : fallbackPriorities;
  const availableStatuses = statuses.length > 0 ? statuses : fallbackStatuses;

  // Show warning if using fallback data
  const usingFallbackData = priorities.length === 0 || statuses.length === 0;

  // Filtered users and teams for search
  const filteredUsers = useMemo(() => {
    if (!userSearchTerm) return users;
    const searchLower = userSearchTerm.toLowerCase();
    return users.filter((user) => {
      const fullName =
        user.first_name || user.last_name
          ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
          : user.email;
      return (
        fullName.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower)
      );
    });
  }, [users, userSearchTerm]);

  const filteredTeams = useMemo(() => {
    if (!teamSearchTerm) return teams;
    const searchLower = teamSearchTerm.toLowerCase();
    return teams.filter((team) =>
      team.name.toLowerCase().includes(searchLower)
    );
  }, [teams, teamSearchTerm]);

  // Click outside handler to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target as Node)
      ) {
        closeUserDropdown();
        setUserSearchTerm("");
      }
      if (
        teamDropdownRef.current &&
        !teamDropdownRef.current.contains(event.target as Node)
      ) {
        closeTeamDropdown();
        setTeamSearchTerm("");
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeUserDropdown();
        closeTeamDropdown();
        setUserSearchTerm("");
        setTeamSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeUserDropdown, closeTeamDropdown]);

  // Helper function to convert hex color to rgba with opacity
  const hexToRgba = (hex: string, opacity: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  // Helper function to get assignee display name
  const getAssigneeDisplayName = useCallback(
    (task: Task) => {
      if (task.assignee_type === "User") {
        const user = users.find((u) => u.id === task.assignee);
        if (user) {
          return user.first_name || user.last_name
            ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
            : user.email;
        }
      } else if (task.assignee_type === "Team") {
        const team = teams.find((t) => t.id === task.assignee);
        if (team) {
          return team.name;
        }
      }
      return task.assignee_type; // fallback to assignee type
    },
    [users, teams]
  );

  const getUserInitials = (task: Task) => {
    const displayName = getAssigneeDisplayName(task);
    return displayName
      .split(" ")
      .map((word: string) => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");
  };

  // Helper function to generate avatar color based on name
  const getAvatarColor = (task: Task) => {
    const displayName = getAssigneeDisplayName(task);
    const colors = [
      "#FF6B6B",
      "#4ECDC4",
      "#45B7D1",
      "#96CEB4",
      "#FFEAA7",
      "#DDA0DD",
      "#98D8C8",
      "#F7DC6F",
      "#BB8FCE",
      "#85C1E9",
    ];
    const index = displayName.length % colors.length;
    return colors[index];
  };

  // Sorting function
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Sort tasks based on current sort field and direction
  const sortTasks = useMemo(() => {
    return (tasks: Task[]) => {
      if (!sortField) return tasks;

      return [...tasks].sort((a, b) => {
        let aValue: string | number;
        let bValue: string | number;

        switch (sortField) {
          case "title":
            aValue = a.title.toLowerCase();
            bValue = b.title.toLowerCase();
            break;
          case "status":
            aValue = a.status?.label.toLowerCase() || "";
            bValue = b.status?.label.toLowerCase() || "";
            break;
          case "priority":
            aValue = a.priority?.label.toLowerCase() || "";
            bValue = b.priority?.label.toLowerCase() || "";
            break;
          case "assignee":
            aValue = getAssigneeDisplayName(a).toLowerCase();
            bValue = getAssigneeDisplayName(b).toLowerCase();
            break;
          case "asset_id":
            aValue = a.asset_id || "";
            bValue = b.asset_id || "";
            break;
          case "due_date":
            aValue = a.due_date ? new Date(a.due_date).getTime() : 0;
            bValue = b.due_date ? new Date(b.due_date).getTime() : 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortDirection === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortDirection === "asc" ? 1 : -1;
        }
        return 0;
      });
    };
  }, [sortField, sortDirection, getAssigneeDisplayName]);
  const filteredTasks = useMemo(() => {
    const filtered = tasks.filter((task) => {
      const matchesSearch =
        task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesPriority =
        !selectedPriority || task.priority_id === selectedPriority;
      const matchesStatus =
        !selectedStatus || task.status_id === selectedStatus;

      // Filter by user (only if assignee_type is "User")
      const matchesUser =
        !selectedUser ||
        (task.assignee_type === "User" && task.assignee === selectedUser);

      // Filter by team (only if assignee_type is "Team")
      const matchesTeam =
        !selectedTeam ||
        (task.assignee_type === "Team" && task.assignee === selectedTeam);

      return (
        matchesSearch &&
        matchesPriority &&
        matchesStatus &&
        matchesUser &&
        matchesTeam
      );
    });

    // Apply sorting to filtered results
    return sortTasks(filtered);
  }, [
    tasks,
    searchTerm,
    selectedPriority,
    selectedStatus,
    selectedUser,
    selectedTeam,
    sortTasks,
  ]);

  const handleDeleteTask = async (taskId: string) => {
    if (window.confirm("Are you sure you want to delete this task?")) {
      try {
        await deleteTask(taskId);
      } catch (error) {
        console.error("Failed to delete task:", error);
      }
    }
  };

  const handleZoomToTask = (task: Task) => {
    if (!task.geom) {
      console.warn("Task has no geometry to zoom to");
      return;
    }
    zoomToTask(task, mapInstance.current);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  // Refresh tasks from backend
  const refreshTasks = useCallback(async () => {
    try {
      await loadTasks();
    } catch (error) {
      console.error("Error refreshing tasks:", error);
    }
  }, [loadTasks]);

  // Export filtered tasks to CSV
  const handleExportCsv = useCallback(() => {
    if (filteredTasks.length === 0) {
      // Nothing to export
      return;
    }

    // Columns restricted to provided list (each header corresponds positionally to the value pushed below)
    const headers = [
      "id",
      "title",
      "description",
      "assignee_type",
      "assignee_name",
      "category_label",
      "status_label",
      "priority_label",
      "layer_name",
      "asset_field",
      "asset_id",
      "geom_json",
      "due_date",
      "start_date",
      "completed_at",
      "photos",
      "attachments",
      "steps_count",
      "steps_json",
      "created_by",
      "created_at",
      "updated_at",
    ];

    const escapeCsv = (value: unknown): string => {
      if (value === null || value === undefined) return "";
      const str = String(value);
      // Wrap in quotes if contains quote, comma, or newline; escape internal quotes
      if (/[",\n]/.test(str)) {
        return '"' + str.replace(/"/g, '""') + '"';
      }
      return str;
    };

    const rows = filteredTasks.map((t) => {
      const assigneeName = getAssigneeDisplayName(t);
      const geomJson = t.geom ? JSON.stringify(t.geom) : "";
      const photos = Array.isArray(t.photos) ? t.photos.join("|") : "";
      const attachments = Array.isArray(t.attachments)
        ? t.attachments.join("|")
        : "";
      const stepsCount = Array.isArray(t.steps) ? t.steps.length : 0;
      const stepsJson = t.steps
        ? JSON.stringify(
            t.steps.map((s) => ({
              id: s.id,
              step_name: s.step_name,
              sequence: s.sequence,
              step_order: s.step_order,
              description: s.description,
              assignee_type: s.assignee_type,
              assignee: s.assignee,
              status_id: s.status_id,
              status_label: s.status?.label,
              is_completed: s.is_completed,
              completed_at: s.completed_at,
              completed_by: s.completed_by,
              reference_link: s.reference_link,
              survey_link: s.survey_link,
              photos: s.photos || [],
              notes: s.notes,
              created_at: s.created_at,
              updated_at: s.updated_at,
            }))
          )
        : "";
      // Order matches headers exactly
      return [
        t.id,
        t.title || "",
        t.description || "",
        t.assignee_type || "",
        assigneeName,
        t.category?.label || "",
        t.status?.label || "",
        t.priority?.label || "",
        t.layer_name || "",
        t.asset_field || "",
        t.asset_id || "",
        geomJson,
        t.due_date || "",
        t.start_date || "",
        t.completed_at || "",
        photos,
        attachments,
        String(stepsCount),
        stepsJson,
        t.created_by || "",
        t.created_at || "",
        t.updated_at || "",
      ]
        .map(escapeCsv)
        .join(",");
    });

    const csvContent = [headers.join(","), ...rows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const dateStamp = new Date().toISOString().split("T")[0];
    a.href = url;
    a.download = `tasks_export_${dateStamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [filteredTasks, getAssigneeDisplayName]);

  // Handle task click from calendar - both view task and close modal
  const handleCalendarTaskClick = useCallback(
    (task: Task) => {
      onViewTask(task);
      closeCalendar();
    },
    [onViewTask, closeCalendar]
  );

  // Sortable header component
  const SortableHeader: React.FC<{
    field: string;
    children: React.ReactNode;
    className?: string;
  }> = ({ field, children, className = "" }) => {
    const isSorted = sortField === field;
    const isAsc = isSorted && sortDirection === "asc";
    return (
      <div
        className={`h-full flex items-center pl-2 py-2.5 cursor-pointer hover:bg-gray-200 transition-colors ${className} ${
          field === "title" ? "rounded-l-lg" : ""
        }`}
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          <span className="font-['Open_Sans'] font-semibold text-[12px] text-[#444444] leading-[1.4]">
            {children}
          </span>
          {isSorted ? (
            isAsc ? (
              <TbChevronUp size={16} className="text-[#444444]" />
            ) : (
              <TbChevronDown size={16} className="text-[#444444]" />
            )
          ) : (
            <TbChevronDown size={16} className="text-gray-300" />
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-white h-full rounded-[20px] shadow border border-gray-200 flex flex-col overflow-hidden">
      {/* Fallback data warning */}
      {usingFallbackData && (
        <div className="p-3 bg-yellow-50 border-b border-yellow-200">
          <p className="text-sm text-yellow-800">
            ⚠️ Using default priorities and statuses. Database connection may be
            limited.
          </p>
        </div>
      )}

      {/* Sticky Header with Search and Filters */}
      <div className="bg-white border-b border-gray-200 flex-shrink-0">
        {/* Title Row - First scrollbar */}
        <div className="overflow-x-auto horizontal-scrollbar">
          <div className="flex flex-row items-center justify-between px-4 py-3 min-w-[700px] lg:min-w-[950px]">
            <h2
              className="font-['Open_Sans'] font-bold text-[22px] text-[#444444] leading-[1.4]"
              style={{ fontVariationSettings: "'wdth' 100" }}
            >
              Tasks
            </h2>
            <div className="flex flex-row items-center gap-2 lg:gap-4">
              {/* Calendar View Button */}
              <button
                onClick={() => {
                  // eslint-disable-next-line no-console
                  console.log("[TaskTable] Calendar button clicked");
                  toggleCalendar();
                }}
                className="bg-[#f3f4f6] cursor-pointer hover:bg-[#ececec] rounded-full flex flex-row items-center justify-center px-2 py-[13px] min-w-[44px] transition-colors group"
                title="View tasks in calendar"
              >
                <TbCalendar size={16} className="text-[#828282]" />
              </button>
              {/* Search - Always visible */}
              <div className="relative w-[180px] lg:w-[299px]">
                <div className="bg-gray-100 rounded-[10px] flex flex-row items-center px-3.5 py-[13px]">
                  <div className="flex flex-row gap-2 items-center w-full">
                    <TbSearch
                      size={16}
                      className="text-[#828282] flex-shrink-0"
                    />
                    <input
                      type="text"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="bg-transparent font-['Open_Sans'] font-medium text-[12px] text-[#828282] leading-[1.5] border-none outline-none placeholder-[#828282] flex-1 min-w-0"
                    />
                  </div>
                </div>
              </div>
              {/* Filters - Always visible */}
              <div className="flex flex-row items-center gap-2 lg:gap-4">
                {/* Priority Filter */}
                <div className="relative">
                  <div className="bg-gray-100 rounded-[10px] flex flex-row items-center justify-between px-3.5 py-[13px] min-w-[140px]">
                    <span className="font-['Open_Sans'] font-medium text-[12px] text-[#828282] leading-[1.5]">
                      {selectedPriority
                        ? availablePriorities.find(
                            (p) => p.id === selectedPriority
                          )?.label || "All Priorities"
                        : "All Priorities"}
                    </span>
                    <TbChevronDown
                      size={16}
                      className="text-[#828282] rotate-0"
                    />
                  </div>
                  <select
                    value={selectedPriority}
                    onChange={(e) => setSelectedPriority(e.target.value)}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  >
                    <option value="">All Priorities</option>
                    {availablePriorities.map((priority) => (
                      <option key={priority.id} value={priority.id}>
                        {priority.label}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Status Filter */}
                <div className="relative">
                  <div className="bg-gray-100 rounded-[10px] flex flex-row items-center justify-between px-3.5 py-[13px] min-w-[140px]">
                    <span className="font-['Open_Sans'] font-medium text-[12px] text-[#828282] leading-[1.5]">
                      {selectedStatus
                        ? availableStatuses.find((s) => s.id === selectedStatus)
                            ?.label || "All Statuses"
                        : "All Statuses"}
                    </span>
                    <TbChevronDown
                      size={16}
                      className="text-[#828282] rotate-0"
                    />
                  </div>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  >
                    <option value="">All Statuses</option>
                    {availableStatuses.map((status) => (
                      <option key={status.id} value={status.id}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>
                {/* User Filter */}
                <div className="relative" ref={userDropdownRef}>
                  <div
                    className="bg-gray-100 rounded-[10px] flex flex-row items-center justify-between px-3.5 py-[13px] min-w-[140px] cursor-pointer"
                    onClick={() => {
                      toggleUserDropdown();
                    }}
                  >
                    <span className="font-['Open_Sans'] font-medium text-[12px] text-[#828282] leading-[1.5] truncate">
                      {selectedUser
                        ? (() => {
                            const user = users.find(
                              (u) => u.id === selectedUser
                            );
                            if (!user) return "All Users";
                            const fullName =
                              user.first_name || user.last_name
                                ? `${user.first_name || ""} ${
                                    user.last_name || ""
                                  }`.trim()
                                : user.email;
                            return fullName.length > 15
                              ? `${fullName.substring(0, 15)}...`
                              : fullName;
                          })()
                        : "All Users"}
                    </span>
                    <TbChevronDown
                      size={16}
                      className={`text-[#828282] transition-transform ${
                        isUserDropdownOpen ? "rotate-180" : "rotate-0"
                      }`}
                    />
                  </div>

                  {/* Searchable Dropdown */}
                  {isUserDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-[10px] shadow-lg z-50 max-h-60 flex flex-col">
                      {/* Search Input */}
                      <div className="p-2 border-b border-gray-100">
                        <div className="relative">
                          <TbSearch
                            size={14}
                            className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400"
                          />
                          <input
                            type="text"
                            placeholder="Search users..."
                            value={userSearchTerm}
                            onChange={(e) => setUserSearchTerm(e.target.value)}
                            className="w-full pl-7 pr-3 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>

                      {/* Options List */}
                      <div className="flex-1 overflow-y-auto">
                        <div
                          className="px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                          onClick={() => {
                            setSelectedUser("");
                            closeUserDropdown();
                            setUserSearchTerm("");
                          }}
                        >
                          All Users
                        </div>
                        {filteredUsers.map((user) => (
                          <div
                            key={user.id}
                            className="px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 cursor-pointer"
                            onClick={() => {
                              setSelectedUser(user.id);
                              closeUserDropdown();
                              setUserSearchTerm("");
                            }}
                          >
                            {user.first_name || user.last_name
                              ? `${user.first_name || ""} ${
                                  user.last_name || ""
                                }`.trim()
                              : user.email}
                          </div>
                        ))}
                        {filteredUsers.length === 0 && userSearchTerm && (
                          <div className="px-3 py-2 text-xs text-gray-400 italic">
                            No users found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {/* Team Filter */}
                <div className="relative" ref={teamDropdownRef}>
                  <div
                    className="bg-gray-100 rounded-[10px] flex flex-row items-center justify-between px-3.5 py-[13px] min-w-[140px] cursor-pointer"
                    onClick={() => {
                      toggleTeamDropdown();
                    }}
                  >
                    <span className="font-['Open_Sans'] font-medium text-[12px] text-[#828282] leading-[1.5] truncate">
                      {selectedTeam
                        ? (() => {
                            const team = teams.find(
                              (t) => t.id === selectedTeam
                            );
                            if (!team) return "All Teams";
                            return team.name.length > 15
                              ? `${team.name.substring(0, 15)}...`
                              : team.name;
                          })()
                        : "All Teams"}
                    </span>
                    <TbChevronDown
                      size={16}
                      className={`text-[#828282] transition-transform ${
                        isTeamDropdownOpen ? "rotate-180" : "rotate-0"
                      }`}
                    />
                  </div>

                  {/* Searchable Dropdown */}
                  {isTeamDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-[10px] shadow-lg z-50 max-h-60 flex flex-col">
                      {/* Search Input */}
                      <div className="p-2 border-b border-gray-100">
                        <div className="relative">
                          <TbSearch
                            size={14}
                            className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400"
                          />
                          <input
                            type="text"
                            placeholder="Search teams..."
                            value={teamSearchTerm}
                            onChange={(e) => setTeamSearchTerm(e.target.value)}
                            className="w-full pl-7 pr-3 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>

                      {/* Options List */}
                      <div className="flex-1 overflow-y-auto">
                        <div
                          className="px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                          onClick={() => {
                            setSelectedTeam("");
                            closeTeamDropdown();
                            setTeamSearchTerm("");
                          }}
                        >
                          All Teams
                        </div>
                        {filteredTeams.map((team) => (
                          <div
                            key={team.id}
                            className="px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 cursor-pointer"
                            onClick={() => {
                              setSelectedTeam(team.id);
                              closeTeamDropdown();
                              setTeamSearchTerm("");
                            }}
                          >
                            {team.name}
                          </div>
                        ))}
                        {filteredTeams.length === 0 && teamSearchTerm && (
                          <div className="px-3 py-2 text-xs text-gray-400 italic">
                            No teams found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Refresh Button */}
              <button
                onClick={refreshTasks}
                className="bg-[#f3f4f6] cursor-pointer hover:bg-[#ececec] rounded-full flex flex-row items-center justify-center px-2 py-[13px] min-w-[44px] transition-colors group"
                title="Refresh tasks"
              >
                <TbRefresh size={16} className="text-[#828282]" />
              </button>
              {/* Export CSV Button */}
              <button
                onClick={handleExportCsv}
                disabled={filteredTasks.length === 0}
                className={`bg-[#f3f4f6] cursor-pointer rounded-full flex flex-row items-center justify-center px-2 py-[13px] min-w-[44px] transition-colors group ${
                  filteredTasks.length === 0
                    ? "opacity-40 cursor-not-allowed"
                    : "hover:bg-[#ececec]"
                }`}
                title={
                  filteredTasks.length === 0
                    ? "No tasks to export"
                    : "Export filtered tasks to CSV"
                }
              >
                <TbDownload size={16} className="text-[#828282]" />
              </button>
              <button
                onClick={setTableVisible.bind(null, false)}
                className="rounded-full transition-colors bg-gray-100 cursor-pointer p-[13px] hover:bg-[#ececec]"
                title="Close"
              >
                <TbX size={16} className="text-[#828282]" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table Header + Content - Second scrollbar */}
      <div className="flex-1 overflow-auto horizontal-scrollbar">
        <div className="min-w-[700px] lg:min-w-[950px]">
          {/* Column Header Row */}
          <div className="px-5 pb-2 sticky top-0 bg-white z-10">
            <div className="bg-gray-100 rounded-lg h-10 flex flex-row items-center">
              {/* Task Column */}
              <SortableHeader field="title" className="flex-1 min-w-30 px-3">
                Task
              </SortableHeader>
              {/* Status Column */}
              <SortableHeader field="status" className="w-[130px]">
                Status
              </SortableHeader>
              {/* Priority Column */}
              <SortableHeader field="priority" className="w-[129px]">
                Priority
              </SortableHeader>
              {/* Assignee Column */}
              <SortableHeader field="assignee" className="w-[200px]">
                Assignee
              </SortableHeader>
              {/* Asset ID Column */}
              <SortableHeader field="asset_id" className="w-[150px]">
                Asset ID
              </SortableHeader>
              {/* Due Date Column */}
              <SortableHeader field="due_date" className="w-[129.429px]">
                Due date
              </SortableHeader>
              {/* Action Column - Not sortable */}
              <div className="w-[89px] h-full flex items-center justify-end pr-3 py-2.5">
                <span className="font-['Open_Sans'] font-semibold text-[12px] text-[#444444] leading-[1.4]">
                  Action
                </span>
              </div>
            </div>
          </div>

          {/* Table Content */}
          <div className="p-5 pt-0">
            {/* Table Container */}
            <div className="bg-white flex flex-col gap-3">
              {/* Table Body */}
              <div className="flex flex-col">
                {filteredTasks.length === 0 ? (
                  <div className="h-[54px] flex items-center justify-center border-b border-gray-100">
                    <span className="text-center text-gray-400 text-base">
                      {tasks.length === 0
                        ? "No tasks found. Create your first task!"
                        : "No tasks match your filters."}
                    </span>
                  </div>
                ) : (
                  filteredTasks.map((task) => (
                    <div
                      key={task.id}
                      className="h-[54px] flex flex-row items-center border-b border-gray-100 hover:bg-blue-50/60 transition cursor-pointer group relative"
                      onClick={() => onTaskSelect?.(task)}
                    >
                      {/* Task Column */}
                      <div className="flex-1 min-w-30 h-full flex items-center px-3 py-2.5">
                        <span className="font-['Open_Sans'] font-semibold text-[14px] text-[#444444] leading-[1.4] truncate">
                          {task.title}
                        </span>
                      </div>
                      {/* Status Column */}
                      <div className="w-[130px] h-full flex items-center pr-2 py-2.5">
                        {task.status && (
                          <div
                            className="inline-flex items-center justify-center px-2 py-1 rounded-[26px] font-['Open_Sans'] font-bold text-[12px] leading-[1.4] uppercase"
                            style={{
                              backgroundColor: hexToRgba(
                                task.status.color || "#000000",
                                0.08
                              ),
                              color: task.status.color,
                              letterSpacing: "0.12px",
                              fontVariationSettings: "'width' 100",
                            }}
                          >
                            {task.status.label}
                          </div>
                        )}
                      </div>
                      {/* Priority Column */}
                      <div className="w-[129px] h-full flex items-center pr-2 py-2.5">
                        {task.priority && (
                          <div
                            className="inline-flex items-center justify-center px-2.5 pb-[5px] pt-[3px] rounded-[20px] font-['Open_Sans'] font-semibold text-[12px] leading-[1.3]"
                            style={{
                              backgroundColor: hexToRgba(
                                task.priority.color || "#000000",
                                0.08
                              ),
                              color: task.priority.color,
                            }}
                          >
                            {task.priority.label}
                          </div>
                        )}
                      </div>
                      {/* Assignee Column */}
                      <div className="w-[200px] h-full flex items-center py-2.5 gap-2.5">
                        {/* User Avatar */}
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-semibold"
                          style={{
                            backgroundColor: getAvatarColor(task),
                          }}
                        >
                          {getUserInitials(task)}
                        </div>
                        <span className="font-['Open_Sans'] font-semibold text-[14px] text-[#444444] leading-[1.4] truncate">
                          {getAssigneeDisplayName(task)}
                        </span>
                      </div>
                      {/* Asset ID Column */}
                      <div className="w-[150px] h-full flex items-center py-2.5">
                        <span className="font-['Open_Sans'] font-semibold text-[14px] text-[#444444] leading-[1.4]">
                          {task.asset_id || "—"}
                        </span>
                      </div>
                      {/* Due Date Column */}
                      <div className="w-[129.429px] h-full flex items-center py-2.5">
                        <span className="font-['Open_Sans'] font-semibold text-[14px] text-[#444444] leading-[1.4]">
                          {formatDate(task.due_date)}
                        </span>
                      </div>
                      {/* Action Column */}
                      <div className="w-[89px] h-full flex items-center justify-end pr-3 py-2">
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // You can implement a dropdown menu here
                            }}
                            className="p-1 hover:bg-gray-100 rounded transition"
                            title="More actions"
                          >
                            <TbDotsVertical
                              size={24}
                              className="text-[#444444]"
                            />
                          </button>
                          {/* Hidden action buttons for now - replace the dots with actual actions */}
                          <div className="hidden group-hover:flex absolute right-0 top-0 bg-white shadow-lg rounded-lg border border-gray-200 z-10">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onViewTask(task);
                              }}
                              className="text-blue-600 hover:text-blue-800 p-2 rounded hover:bg-blue-100 transition"
                              title="View Task"
                            >
                              <TbEye size={16} />
                            </button>
                            {task.geom && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleZoomToTask(task);
                                }}
                                className="text-orange-600 hover:text-orange-800 p-2 rounded hover:bg-orange-100 transition"
                                title="Zoom to Feature"
                              >
                                <TbFocus2 size={16} />
                              </button>
                            )}
                            {/* <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditTask(task);
                          }}
                          className="text-green-600 hover:text-green-800 p-2 rounded hover:bg-green-100 transition"
                          title="Edit Task"
                        >
                          <TbEdit size={16} />
                        </button> */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTask(task.id);
                              }}
                              className="text-red-600 hover:text-red-800 p-2 rounded hover:bg-red-100 transition"
                              title="Delete Task"
                            >
                              <TbTrash size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Modal */}
      <CalendarModal
        open={isCalendarOpen}
        onOpenChange={(open) => !open && closeCalendar()}
        tasks={filteredTasks}
        onTaskClick={handleCalendarTaskClick}
      />
    </div>
  );
};
